import React, { useState, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatFileSize, formatDateTime } from '@core/utils/helpers';
import { fastLanerService } from '@/services';
import NewWcnTable, { DataTableColumn, DataTablePagination } from '@/components/datatable/NewWcnTable.tsx';
import { TableQuery, TableQueryEvent } from '@/components/datatable/tableTypes';
import { ApkItem, ApkListRef } from './types.ts';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';

// Define clear sorting map for API calling
const API_SORT_MAP: Record<string, string> = {
    packageName: 'PACKAGE_NAME',
    fileName: 'FILE_NAME',
    version: 'VERSION',
    uploadDate: 'UPLOAD_DATE'
};

// Defined default table data querying
const INITIAL_QUERY: TableQuery = {
    sortBy: 'packageName',
    sortDirection: 'asc',
    page: 0,
    pageSize: 10
};

interface ApkListProps {
    fastLanerOrgId: string | null;
    selectable?: boolean;
    value?: ApkItem[];
    onChange?: (selected: ApkItem[]) => void;
    onLoading?: () => void;
    onLoaded?: () => void;
}

const ApkList = forwardRef<ApkListRef, ApkListProps>(
    ({ fastLanerOrgId, selectable = false, value = [], onChange, onLoading, onLoaded }, ref) => {
        const { t } = useTranslation();
        const [apks, setApks] = useState<ApkItem[]>([]);
        const [total, setTotal] = useState(0);
        const [loading, setLoading] = useState(false);
        const [query, setQuery] = useState<TableQuery>(INITIAL_QUERY);

        const load = useCallback(
            async (q: TableQuery = query) => {
                if (!fastLanerOrgId) return;

                setLoading(true);
                onLoading?.();

                try {
                    const params = new URLSearchParams({
                        sortBy: API_SORT_MAP[q.sortBy] ?? q.sortBy.toUpperCase(),
                        sortDirection: q.sortDirection === 'desc' ? 'DESC' : 'ASC',
                        pageSize: q.pageSize.toString(),
                        pageIndex: (q.page + 1).toString()
                    });

                    const data = await fastLanerService.loadApkList(fastLanerOrgId, { params });

                    setApks(data.data);
                    setTotal(data.pagination.totalCount);
                } catch (error) {
                    console.error('Failed to fetch APK list:', error);
                    setApks([]);
                    setTotal(0);
                } finally {
                    setLoading(false);
                    onLoaded?.();
                }
            },
            [fastLanerOrgId, query]
        );

        useEffect(() => {
            const unsubscribeReload = eventBus.on(EventTypes.WIDGETS_RELOAD, () => load());
            load();
            return () => {
                unsubscribeReload();
            };
        }, [load]);

        useImperativeHandle(ref, () => ({
            load: () => load()
        }));

        // Define columns based on selectable mode
        const columns: DataTableColumn<ApkItem>[] = selectable
            ? [
                  {
                      text: t('packageName'),
                      value: 'packageName',
                      sortable: true,
                      width: 250
                  },
                  {
                      text: t('version'),
                      value: 'version',
                      sortable: true,
                      width: 200
                  },
                  {
                      text: t('fileSize'),
                      value: 'fileSize',
                      sortable: false,
                      width: 100,
                      render: (row) => formatFileSize(row.fileSize)
                  },
                  {
                      text: t('uploadDate'),
                      value: 'uploadDate',
                      sortable: true,
                      width: 200,
                      render: (row) => formatDateTime(row.uploadDate)
                  }
              ]
            : [
                  {
                      text: t('packageName'),
                      value: 'packageName',
                      sortable: true,
                      width: 200,
                      render: (row) => row.packageName
                  },
                  {
                      text: t('fileName'),
                      value: 'fileName',
                      sortable: true,
                      width: 100,
                      render: (row) => row.fileName
                  },
                  {
                      text: t('version'),
                      value: 'version',
                      sortable: true,
                      width: 100,
                      render: (row) => row.version
                  },
                  {
                      text: t('fileSize'),
                      value: 'fileSize',
                      sortable: false,
                      width: 100,
                      render: (row) => formatFileSize(row.fileSize)
                  },
                  {
                      text: t('uploadDate'),
                      value: 'uploadDate',
                      sortable: true,
                      width: 100,
                      render: (row) => formatDateTime(row.uploadDate)
                  }
              ];

        return (
            <NewWcnTable<ApkItem>
                title={selectable ? t('fastLanerAppVersionSelection') : undefined}
                columns={columns}
                data={apks}
                loading={loading}
                sorting={{
                    sortBy: query.sortBy,
                    sortDirection: query.sortDirection
                }}
                pagination={{
                    count: total,
                    page: query.page,
                    rowsPerPage: query.pageSize,
                    rowsPerPageOptions: [10, 15, 30]
                }}
                onQueryChange={(q, event) => {
                    // Reset to page 0 on sort or page size change; keep page on page change
                    const next = event === TableQueryEvent.PAGE ? q : { ...q, page: 0 };
                    setQuery(next);
                    load(next);
                }}
                selection={
                    selectable
                        ? {
                              selectedItems: value,
                              onSelectionChange: onChange || (() => {}),
                              getItemKey: (item) => item.fileName,
                              selectionMode: 'single'
                          }
                        : undefined
                }
                noDataMessage={t('noData')}
                size="small"
            />
        );
    }
);

ApkList.displayName = 'ApkList';

export default ApkList;
